import {
  Award,
  BookOpen,
  Briefcase,
  FolderOpen,
  Globe,
  GraduationCap,
  Heart,
  MessageSquareQuote,
  Star,
  User,
  Wrench,
} from "lucide-react";

type Json = Record<string, unknown>;

function isNonEmpty(value: unknown): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return true;
}

function str(obj: Json, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function dateText(obj: Json, key: string): string | undefined {
  const v = obj[key];
  if (typeof v === "string") return v;
  if (v && typeof v === "object" && "text" in (v as Json)) {
    return (v as Json).text as string;
  }
  return undefined;
}

function stableKey(item: unknown, i: number): string {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    const o = item as Json;
    const parts = [o.title, o.name, o.role, o.givenBy, o.position, o.link].filter(Boolean);
    if (parts.length > 0) return parts.join("-");
  }
  return String(i);
}

function itemLabel(item: unknown): string {
  if (typeof item === "string") return item;
  if (item && typeof item === "object" && "name" in (item as Json)) {
    return ((item as Json).name as string) ?? JSON.stringify(item);
  }
  return JSON.stringify(item);
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asRecords(value: unknown): Json[] {
  return asArray(value).filter((item): item is Json => !!item && typeof item === "object");
}

const PILL_CLASS =
  "rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300";

/**
 * The profile panel is an allowlist: only the sections listed here are ever
 * rendered. The raw scraper payload stored in `profile_data` carries plenty
 * of keys we deliberately do not show (cover pictures, interests, follower
 * counts, section totals, internal ids, ...) and any key the scraper adds in
 * future is ignored until someone builds a section for it. `keys` lists the
 * accepted aliases for a section; the first non-empty one wins.
 */
interface ProfileSection {
  id: string;
  keys: string[];
  title: string;
  icon: React.ReactNode;
  render: (value: unknown) => React.ReactNode;
}

const SECTIONS: ProfileSection[] = [
  {
    id: "summary",
    keys: ["about", "summary"],
    title: "Summary",
    icon: <User size={16} />,
    render: (value) =>
      typeof value === "string" ? (
        <p className="whitespace-pre-wrap text-sm text-text-secondary">{value}</p>
      ) : null,
  },
  {
    id: "experience",
    keys: ["experience", "work_experience"],
    title: "Experience",
    icon: <Briefcase size={16} />,
    render: (value) => (
      <div className="space-y-3">
        {asRecords(value).map((job, i) => (
          <ExperienceEntry key={experienceKey(job, i)} job={job} />
        ))}
      </div>
    ),
  },
  {
    id: "education",
    keys: ["education"],
    title: "Education",
    icon: <GraduationCap size={16} />,
    render: (value) => (
      <div className="space-y-2">
        {asRecords(value).map((edu, i) => (
          <EducationEntry key={educationKey(edu, i)} edu={edu} />
        ))}
      </div>
    ),
  },
  {
    id: "skills",
    keys: ["skills"],
    title: "Skills",
    icon: <Wrench size={16} />,
    render: (value) => (
      <div className="flex flex-wrap gap-1.5">
        {asArray(value).map((skill, i) => {
          const label = itemLabel(skill);
          return (
            <span key={stableKey(skill, i)} className={PILL_CLASS}>
              {label}
            </span>
          );
        })}
      </div>
    ),
  },
  {
    id: "certifications",
    keys: ["certifications"],
    title: "Certifications",
    icon: <Award size={16} />,
    render: (value) => (
      <div className="space-y-2">
        {asArray(value).map((cert, i) => (
          <CertificationEntry key={stableKey(cert, i)} cert={cert} />
        ))}
      </div>
    ),
  },
  {
    id: "languages",
    keys: ["languages"],
    title: "Languages",
    icon: <Globe size={16} />,
    render: (value) => (
      <div className="flex flex-wrap gap-1.5">
        {asArray(value).map((lang, i) => {
          const label = itemLabel(lang);
          const proficiency =
            lang && typeof lang === "object"
              ? ((lang as Json).proficiency as string | undefined)
              : undefined;
          return (
            <span key={stableKey(lang, i)} className={PILL_CLASS} title={proficiency ?? undefined}>
              {label}
              {proficiency && (
                <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                  ({proficiency})
                </span>
              )}
            </span>
          );
        })}
      </div>
    ),
  },
  {
    id: "publications",
    keys: ["publications"],
    title: "Publications",
    icon: <BookOpen size={16} />,
    render: (value) => (
      <div className="space-y-3">
        {asRecords(value).map((pub, i) => (
          <PublicationEntry key={stableKey(pub, i)} pub={pub} />
        ))}
      </div>
    ),
  },
  {
    id: "projects",
    keys: ["projects"],
    title: "Projects",
    icon: <FolderOpen size={16} />,
    render: (value) => (
      <div className="space-y-3">
        {asRecords(value).map((proj, i) => (
          <ProjectEntry key={stableKey(proj, i)} proj={proj} />
        ))}
      </div>
    ),
  },
  {
    id: "volunteering",
    keys: ["volunteering"],
    title: "Volunteering",
    icon: <Heart size={16} />,
    render: (value) => (
      <div className="space-y-3">
        {asRecords(value).map((vol, i) => (
          <VolunteeringEntry key={stableKey(vol, i)} vol={vol} />
        ))}
      </div>
    ),
  },
  {
    id: "honors",
    keys: ["honorsAndAwards", "honors_and_awards"],
    title: "Honors & Awards",
    icon: <Star size={16} />,
    render: (value) => (
      <div className="space-y-2">
        {asRecords(value).map((award, i) => (
          <HonorEntry key={stableKey(award, i)} award={award} />
        ))}
      </div>
    ),
  },
  {
    id: "recommendations",
    keys: ["receivedRecommendations", "received_recommendations"],
    title: "Recommendations",
    icon: <MessageSquareQuote size={16} />,
    render: (value) => (
      <div className="space-y-4">
        {asRecords(value).map((rec, i) => (
          <RecommendationEntry key={stableKey(rec, i)} rec={rec} />
        ))}
      </div>
    ),
  },
  {
    id: "causes",
    keys: ["causes"],
    title: "Causes",
    icon: <Heart size={16} />,
    render: (value) => (
      <div className="flex flex-wrap gap-1.5">
        {asArray(value).map((cause, i) => (
          <span key={stableKey(cause, i)} className={PILL_CLASS}>
            {itemLabel(cause)}
          </span>
        ))}
      </div>
    ),
  },
];

function firstNonEmpty(data: Json, keys: string[]): unknown {
  for (const key of keys) {
    if (isNonEmpty(data[key])) return data[key];
  }
  return undefined;
}

/**
 * LinkedIn "Services" is where freelancers and consultants advertise what
 * they offer for hire. It is noise as a section, but a weak signal that the
 * person is consulting or available rather than permanently employed, so it
 * is surfaced as a single flag line. Returns null when there is nothing to
 * flag, otherwise the service names we could extract (possibly none).
 */
export function serviceNames(value: unknown): string[] | null {
  if (!isNonEmpty(value)) return null;
  const nameOf = (item: unknown): string | undefined => {
    if (typeof item === "string") return item.trim() || undefined;
    if (item && typeof item === "object") return str(item as Json, "name", "title", "serviceName");
    return undefined;
  };
  const items = Array.isArray(value) ? value : [value];
  return items.map(nameOf).filter((name): name is string => !!name);
}

export function ProfileDataPanel({ profileData }: { profileData: Json | null }) {
  if (!profileData || Object.keys(profileData).length === 0) return null;

  const sections = SECTIONS.flatMap((section) => {
    const value = firstNonEmpty(profileData, section.keys);
    return value === undefined ? [] : [{ section, value }];
  });
  const services = serviceNames(profileData.services);

  if (sections.length === 0 && services === null) return null;

  return (
    <div className="mt-6">
      <h2 className="text-lg font-medium text-text-primary">Profile</h2>
      {services && (
        <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-text-muted">
          <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            Offers services on LinkedIn
          </span>
          {services.length > 0 && <span>{services.join(", ")}</span>}
        </p>
      )}
      <div className="mt-2 space-y-4">
        {sections.map(({ section, value }) => (
          <Section key={section.id} icon={section.icon} title={section.title}>
            {section.render(value)}
          </Section>
        ))}
      </div>
    </div>
  );
}

/* --- Entry components --- */

function ExperienceEntry({ job }: { job: Json }) {
  const title = str(job, "position", "title");
  const company = str(job, "companyName", "company");
  const startDate = dateText(job, "startDate") ?? str(job, "start_date");
  const endDate = dateText(job, "endDate") ?? str(job, "end_date");
  const description = str(job, "description");
  const location = str(job, "location");
  const employmentType = str(job, "employmentType", "employment_type");

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          {title && <span className="text-sm font-medium text-text-primary">{title}</span>}
          {company && (
            <span className="text-sm text-text-muted">
              {title ? " at " : ""}
              {company}
            </span>
          )}
          {employmentType && <span className="text-sm text-text-faint"> · {employmentType}</span>}
        </div>
        {(startDate || endDate) && (
          <span className="shrink-0 text-xs text-text-faint">
            {startDate ?? ""}
            {startDate && endDate ? " \u2013 " : ""}
            {endDate ?? "Present"}
          </span>
        )}
      </div>
      {location && <p className="text-xs text-text-faint">{location}</p>}
      {description && (
        <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">{description}</p>
      )}
    </div>
  );
}

function EducationEntry({ edu }: { edu: Json }) {
  const degree = str(edu, "degree", "degreeName");
  const field = str(edu, "field", "fieldOfStudy");
  const institution = str(edu, "institution", "schoolName");
  const startDate = dateText(edu, "startDate") ?? str(edu, "start_date");
  const endDate = dateText(edu, "endDate") ?? str(edu, "end_date");
  const year = edu.year as string | number | undefined;
  const dateDisplay = startDate || endDate ? `${startDate ?? ""} \u2013 ${endDate ?? ""}` : year;

  return (
    <div className="flex items-baseline justify-between gap-2">
      <div>
        {degree && <span className="text-sm font-medium text-text-primary">{degree}</span>}
        {field && (
          <span className="text-sm text-text-muted">
            {degree ? " in " : ""}
            {field}
          </span>
        )}
        {institution && (
          <span className="text-sm text-text-muted">
            {degree || field ? ", " : ""}
            {institution}
          </span>
        )}
      </div>
      {dateDisplay && <span className="shrink-0 text-xs text-text-faint">{dateDisplay}</span>}
    </div>
  );
}

function CertificationEntry({ cert }: { cert: unknown }) {
  if (typeof cert === "string") {
    return <p className="text-sm text-text-secondary">{cert}</p>;
  }
  const c = cert as Json;
  const title = str(c, "title", "name");
  const issuer = str(c, "issuedBy", "issuer");
  const date = str(c, "issuedAt", "date");

  return (
    <div>
      {title && <span className="text-sm font-medium text-text-primary">{title}</span>}
      {issuer && (
        <span className="text-sm text-text-muted">
          {title ? " · " : ""}
          {issuer}
        </span>
      )}
      {date && <span className="ml-1 text-xs text-text-faint">{date}</span>}
    </div>
  );
}

function PublicationEntry({ pub }: { pub: Json }) {
  const title = str(pub, "title");
  const link = str(pub, "link", "url");
  const description = str(pub, "description");
  const publishedAt = str(pub, "publishedAt", "published_at");

  return (
    <div>
      <div>
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-accent hover:underline"
          >
            {title ?? link}
          </a>
        ) : (
          title && <span className="text-sm font-medium text-text-primary">{title}</span>
        )}
        {publishedAt && <span className="ml-2 text-xs text-text-faint">{publishedAt}</span>}
      </div>
      {description && (
        <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">{description}</p>
      )}
    </div>
  );
}

function ProjectEntry({ proj }: { proj: Json }) {
  const title = str(proj, "title");
  const link = str(proj, "link", "url");
  const description = str(proj, "description");
  const duration = str(proj, "duration");

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-accent hover:underline"
            >
              {title ?? link}
            </a>
          ) : (
            title && <span className="text-sm font-medium text-text-primary">{title}</span>
          )}
        </div>
        {duration && <span className="shrink-0 text-xs text-text-faint">{duration}</span>}
      </div>
      {description && (
        <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">{description}</p>
      )}
    </div>
  );
}

function VolunteeringEntry({ vol }: { vol: Json }) {
  const role = str(vol, "role");
  const org = str(vol, "organizationName", "organization");
  const cause = str(vol, "cause");
  const duration = str(vol, "duration");
  const description = str(vol, "description");

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          {role && <span className="text-sm font-medium text-text-primary">{role}</span>}
          {org && (
            <span className="text-sm text-text-muted">
              {role ? " at " : ""}
              {org}
            </span>
          )}
        </div>
        {duration && <span className="shrink-0 text-xs text-text-faint">{duration}</span>}
      </div>
      {cause && <p className="text-xs text-text-faint">{cause}</p>}
      {description && (
        <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">{description}</p>
      )}
    </div>
  );
}

function HonorEntry({ award }: { award: Json }) {
  const title = str(award, "title");
  const issuer = str(award, "issuedBy", "issuer");
  const date = str(award, "issuedAt", "date");
  const assoc = str(award, "associatedWith");

  return (
    <div>
      {title && <span className="text-sm font-medium text-text-primary">{title}</span>}
      {issuer && (
        <span className="text-sm text-text-muted">
          {title ? " · " : ""}
          {issuer}
        </span>
      )}
      {date && <span className="ml-1 text-xs text-text-faint">{date}</span>}
      {assoc && <p className="text-xs text-text-faint">{assoc}</p>}
    </div>
  );
}

function RecommendationEntry({ rec }: { rec: Json }) {
  const givenBy = str(rec, "givenBy");
  const headline = str(rec, "givenByHeadline");
  const description = str(rec, "description");
  const context = str(rec, "givenAt");

  return (
    <div>
      <div>
        {givenBy && <span className="text-sm font-medium text-text-primary">{givenBy}</span>}
        {headline && (
          <span className="text-sm text-text-muted">
            {givenBy ? " · " : ""}
            {headline}
          </span>
        )}
      </div>
      {context && <p className="text-xs text-text-faint">{context}</p>}
      {description && (
        <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">{description}</p>
      )}
    </div>
  );
}

/* --- Shared helpers --- */

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-medium text-text-secondary">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}

function experienceKey(job: Json, i: number): string {
  return `${str(job, "companyName", "company") ?? ""}-${str(job, "position", "title") ?? ""}-${i}`;
}

function educationKey(edu: Json, i: number): string {
  return `${str(edu, "institution", "schoolName") ?? ""}-${str(edu, "degree", "degreeName") ?? ""}-${i}`;
}
